import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Utils } from 'src/app/core/resources/utils';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogComponent } from '../dialog/dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { FileStorage } from 'src/app/core/interfaces/fileStorage';
import { AngularFireStorage } from '@angular/fire/storage';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { BasicPerson } from 'src/app/core/interfaces/basicPerson';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';
import { SatelliteLockType } from 'src/app/core/interfaces/satelliteLockType';
import { SatelliteLockStateDevice } from 'src/app/core/enums/satelliteLockStateDevice.enum';
import { AuthService } from 'src/app/core/services/authentication.service';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Subscription } from 'rxjs';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';
import { CargoDetailService } from 'src/app/modules/cargo/cargo-detail/cargo-detail.service';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import {
    SatelliteLockInspection,
    SatelliteLockQuestion,
    DEVICE_REPORTING_OPTIONS,
    DEVICE_CHARGE_LEVEL_OPTIONS
} from 'src/app/core/interfaces/satelliteLockInspection';


@Component({
    selector: 'app-dialog-satellite-lock',
    templateUrl: './dialog-satellite-lock.component.html',
    styleUrls: ['./dialog-satellite-lock.component.scss'],
})
export class DialogSatelliteLockComponent implements OnInit, OnDestroy {
    permission = Permission;
    form = new FormGroup({ imei: new FormControl('', [Validators.required, Validators.pattern(/^\d{15}$/)]), typeId: new FormControl('', Validators.required), typeName: new FormControl('', Validators.required), firmwareVersion: new FormControl(''), stateDevice: new FormControl(SatelliteLockStateDevice.ACTIVE, Validators.required), description: new FormControl('') });
    preoperationalInspectionForm = new FormGroup({
        deviceReporting: new FormControl('', Validators.required),
        deviceChargeLevel: new FormControl('', Validators.required),
        driverWarranty: new FormControl(false, Validators.required),
    });

    readonly deviceReportingOptions = DEVICE_REPORTING_OPTIONS;
    readonly deviceChargeLevelOptions = DEVICE_CHARGE_LEVEL_OPTIONS;
    approvalForm = new FormGroup({
        approvalUser: new FormControl({ value: '', disabled: true }),
    });

    readonly stateDeviceOptions = [
        { value: SatelliteLockStateDevice.ACTIVE, label: 'Activo' },
        { value: SatelliteLockStateDevice.INACTIVE, label: 'Inactivo' },
        { value: SatelliteLockStateDevice.IN_MAINTENANCE, label: 'En mantenimiento' },
    ];

    optionsSatelliteLockType: OptionsAutocomplete = {
        title: 'Tipo de candado satelital',
        appearance: 'outline'
    };

    public approvalUserInformation: string;
    public createdUserInformation: string;
    public approvalUserControl: FormControl = new FormControl({ value: '', disabled: true });
    public changeApprovalUser: boolean = false;
    public optionsDocumentUser: OptionsAutocomplete = {
        title: 'N° de identificación del aprobador',
        filterBy: 'userClient',
    };
    public changeApprovalUserControl: FormControl = new FormControl('', Validators.required);
    public changeApprovalUserSub: Subscription;
    public approvalUser = {
        name: '',
        document: ''
    };
    instanceLogo: FileStorage;
    basePath: string;

    constructor(
        public dialogRef: MatDialogRef<DialogSatelliteLockComponent>,
        private authService: AuthService,
        private snackBarService: SnackBarService,
        public utils: Utils,
        public dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private angularFireStorage: AngularFireStorage,
        private permissionRole: PermissionRole,
        private cargoDetailService: CargoDetailService,
        private cargoManager: CargoManager,
        @Inject(MAT_DIALOG_DATA) public data: {
            satelliteLock?: SatelliteLock,
            cargo?: Cargo,
            user?: BasicPerson,
            disabled?: boolean,
            id?: string,
            isPreoperationalInspection?: boolean,
            inspection?: SatelliteLockInspection,
            isViewInspection?: boolean,
            canApprove?: boolean,
        }
    ) { }

    async ngOnInit() {
        this.initializeForm();
        this.setupApprovalUser();
        if (this.isPreoperationalInspection) {
            this.createdUserInformation = (this.data.user && this.data.user.name) || this.userName;
        }
        if (this.isViewInspection) this.initializeViewInspectionForm();

    }


    private initializeViewInspectionForm(): void {
        if (!this.data.inspection) return;
        const structure: SatelliteLockQuestion[] = this.data.inspection.satelliteLockStructure || [];
        const deviceReportingQuestion = structure.find((q: SatelliteLockQuestion) => q.question === 'DR');
        const deviceChargeLevelQuestion = structure.find((q: SatelliteLockQuestion) => q.question === 'NDCD');

        this.preoperationalInspectionForm.patchValue({
            deviceReporting: deviceReportingQuestion ? deviceReportingQuestion.response : '',
            deviceChargeLevel: deviceChargeLevelQuestion ? deviceChargeLevelQuestion.response : '',
            driverWarranty: true
        });
        this.preoperationalInspectionForm.disable()
        const evaluatorName = this.utils.getNestedValue(this.data.inspection, 'evaluatorFingerPrint.userName');
        if (evaluatorName) this.createdUserInformation = evaluatorName;
    }


    private initializeForm() {
        if (this.data.satelliteLock) {
            this.form.patchValue({
                imei: this.data.satelliteLock.imei || '',
                typeId: (this.data.satelliteLock.type && this.data.satelliteLock.type.id) || '',
                typeName: (this.data.satelliteLock.type && this.data.satelliteLock.type.name) || '',
                firmwareVersion: this.data.satelliteLock.firmwareVersion || '',
                stateDevice: this.data.satelliteLock.stateDevice || SatelliteLockStateDevice.ACTIVE,
                description: this.data.satelliteLock.description || '',
            });

            if (this.data.disabled) this.form.disable();
        } else this.createdUserInformation = (this.data.user && this.data.user.name) || '';

    }

    private setupApprovalUser() {
        if (this.data.user) {
            this.approvalUser.name = this.data.user.name;
            this.approvalUser.document = this.data.user.document;
            this.approvalUserControl.setValue(this.approvalUser.name);
        }

        this.changeApprovalUserSub = this.changeApprovalUserControl.valueChanges.subscribe(value => {
            if (value && value.information && value.information.document) {
                this.approvalUserControl.setValue(value.information.name);
                this.approvalUser.name = value.information.name;
                this.approvalUser.document = value.information.document;
                this.changeApprovalUser = false;
            }
        });
    }

    public setNewApprovalUser(change: boolean) {
        this.changeApprovalUser = change;
    }

    public approve(approval: boolean) {
        const message = approval ? 'Candado satelital aprobado correctamente' : 'Candado satelital rechazado correctamente';
        this.snackBarService.openSnackBar(message, undefined, "success");
        this.dialogRef.close({ approved: approval });
    }


    get canApproveSatelliteLock(): boolean {
        const hasSatelliteLock = this.utils.getNestedValue(this.data, 'satelliteLock.id');
        const hasPermission = this.permissionRole.hasPermission(
            this.permission.cargo.module,
            this.permission.cargo.editSatelliteLockModule
        );
        return hasSatelliteLock && hasPermission;
    }


    get canRejectSatelliteLock(): boolean {
        const hasSatelliteLock = this.utils.getNestedValue(this.data, 'satelliteLock.id');
        return hasSatelliteLock && this.canApproveSatelliteLock;
    }


    get userName(): string {
        const evaluatorUserName = this.utils.getNestedValue(this.data.satelliteLock, 'evaluatorFingerPrint.userName');
        if (evaluatorUserName) return evaluatorUserName;
        const userSession = this.authService && this.authService.getUserSession();
        return (userSession && userSession.information && userSession.information.name) || '';
    }


    get isPreoperationalInspection(): boolean {
        return !!this.data.isPreoperationalInspection;
    }

    get createdAndApprovedBy(): string {
        return this.createdUserInformation || this.approvalUserInformation || this.userName;
    }


    get isViewInspection(): boolean {
        return !!this.data.isViewInspection;
    }


    get canApproveInspection(): boolean {
        return !!this.data.canApprove;
    }


    get inspectionApprovalStatus(): string {
        if (!this.data.inspection) return 'Sin inspección';
        const approval = this.data.inspection.approval;
        if (approval === true) return 'Aprobado';
        if (approval === false) return 'Rechazado';
        return 'Sin aprobar';
    }


    get showErrorMessages(): string {
        if (this.isViewInspection) return '';
        const deviceReporting = this.preoperationalInspectionForm.get('deviceReporting').value;
        const deviceChargeLevel = this.preoperationalInspectionForm.get('deviceChargeLevel').value;
        if (deviceReporting === 'NO') return 'Tener en cuenta que, si el dispositivo no está reportando, la inspección será rechazada.';
        if (deviceChargeLevel === 'LESS_THAN_25' || deviceChargeLevel === 'GREATER_THAN_25') return 'Tener en cuenta que, si el nivel de carga del dispositivo es menor al 50 %, la inspección será rechazada.';
        return '';
    }

    getOptionLabel(options: Array<{ value: string; label: string }>, value: string): string {
        const option = options.find(opt => opt.value === value);
        return option ? option.label : value || '-';
    }


    async approveRejectedInspection(): Promise<void> {
        if (!this.data.inspection || !this.data.cargo) {
            this.snackBarService.openSnackBar('Información incompleta para aprobar', undefined, 'error');
            return;
        }
        const result = await this.cargoManager.openDialogConfirm('¿Está seguro que desea aprobar la inspección aunque no cumpla con los criterios de aprobación?');
        if (!result.confirm) return;
        const inspectionId = this.data.inspection.id;
        const cargoId = this.data.cargo.id;
        const userSession = this.authService.getUserSession();
        const userName = (userSession && userSession.information && userSession.information.name) || '';

        if (!inspectionId || !cargoId) {
            this.snackBarService.openSnackBar('No se encontró el ID de la inspección o el cargo', undefined, 'error');
            return;
        }

        this.spinner.show();

        this.cargoDetailService.approveSatelliteLockInspection(inspectionId).subscribe(
            () => {
                this.spinner.hide();
                this.snackBarService.openSnackBar('Inspección aprobada correctamente', undefined, 'success');
                this.dialogRef.close({ state: true });
            },
            (error: HttpErrorResponse) => {
                this.spinner.hide();
                const errorMessage = (error && error.error && error.error.message)
                    ? error.error.message
                    : 'Error al aprobar la inspección';
                this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
            }
        );
    }

    /**
     * Guarda la inspección preoperacional del candado satelital
     * Valida el formulario y envía los datos al backend mediante POST
     */
    public savePreoperationalInspection(): void {
        this.preoperationalInspectionForm.markAllAsTouched();
        if (this.utils.errorMessagesCustomized(this.preoperationalInspectionForm.get('deviceReporting'), 'dispositivo reportando')) return;
        if (this.utils.errorMessagesCustomized(this.preoperationalInspectionForm.get('deviceChargeLevel'), 'nivel de carga del dispositivo')) return;
        if (!this.preoperationalInspectionForm.get('driverWarranty').value)
            return this.snackBarService.openSnackBar('Debe aceptar la garantía de responsabilidad', undefined, 'alert');
        if (this.preoperationalInspectionForm.invalid)
            return this.snackBarService.openSnackBar('Por favor complete todos los campos requeridos', undefined, 'alert');

        const cargoId = this.utils.getNestedValue(this.data, 'cargo.id');
        const imei = this.utils.getNestedValue(this.data, 'satelliteLock.imei');
        if (!cargoId || !imei)
            return this.snackBarService.openSnackBar('Información incompleta para guardar la inspección', undefined, 'error');

        const inspectionData = {
            imei: imei,
            version: 1,
            satelliteLockStructure: [
                {
                    question: 'DR',
                    response: this.preoperationalInspectionForm.get('deviceReporting').value
                },
                {
                    question: 'NDCD',
                    response: this.preoperationalInspectionForm.get('deviceChargeLevel').value
                }
            ]
        };

        this.spinner.show();
        this.cargoDetailService.saveSatelliteLockInspection(cargoId, inspectionData).subscribe(
            (response) => {
                this.spinner.hide();
                this.snackBarService.openSnackBar('Inspección preoperacional guardada correctamente');
                this.dialogRef.close({ state: true });
            },
            (error: HttpErrorResponse) => {
                this.spinner.hide();
                const errorMessage = (error && error.error && error.error.message)
                    ? error.error.message
                    : 'Error al guardar la inspección preoperacional';
                this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
            }
        );
    }

    ngOnDestroy() {
        if (this.changeApprovalUserSub) this.changeApprovalUserSub.unsubscribe();

    }
}
